import { memo, useCallback, useMemo } from 'react';

import { type Annotation, type Clip, type CommentResponse, type Discussion as DiscussionType } from '@air/api/types';
import { EllipsisVerticalIcon, MessageCircleCheckIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import { type ClipDiscussion } from '~/components/Annotations/shared/types';
import { DropdownMenuWithActionSheet } from '~/components/Menus/DropdownMenuWithActionSheet';
import { QueryParamNames } from '~/constants/search';
import { DISCUSSION_COMMENT_MENU_TRIGGER } from '~/constants/testIDs';
import { activeDiscussionIdParamSelector } from '~/store/router/selectors';
import { getDiscussionCommentMenuOptions } from '~/utils/menuOptions/getDiscussionCommentMenuOptions';
import { setQueryParams } from '~/utils/PathUtils';
import { useAirStore } from '~/utils/ReduxUtils';

export type CommentMenuProps = {
  onEdit?: () => void;

  onToggleResolve:
    | ((params: {
        id: DiscussionType['id'];
        resolved: DiscussionType['resolved'];
        clipId: Clip['id'];
        timestamp?: number;
        annotation?: Annotation;
      }) => void)
    | undefined;

  onDelete?: (params: {
    comment: CommentResponse;
    clipId: Clip['id'];
    timestamp?: number;
    annotation?: Annotation;
  }) => void;

  discussion: ClipDiscussion;
  comment: CommentResponse;
};

export const CommentMenu = memo(({ onToggleResolve, onEdit, onDelete, discussion, comment }: CommentMenuProps) => {
  const store = useAirStore();

  const handleDeleteClick = useCallback(() => {
    onDelete?.({
      comment,
      clipId: discussion.clipId,
      timestamp: discussion.timestamp,
      annotation: discussion.annotation,
    });
    const activeDiscussionId = activeDiscussionIdParamSelector(store.getState());

    if (activeDiscussionId === discussion.id) {
      setQueryParams({ [QueryParamNames.discussionId]: undefined }, 'replace');
    }
  }, [comment, discussion, onDelete, store]);

  const handleToggleResolveClick = useCallback(() => {
    onToggleResolve?.({
      clipId: discussion.clipId,
      timestamp: discussion.timestamp,
      annotation: discussion.annotation,
      id: discussion.id,
      resolved: !discussion.resolved,
    });
  }, [discussion, onToggleResolve]);

  const menuOptions = useMemo(
    () =>
      getDiscussionCommentMenuOptions({
        onDelete: onDelete ? handleDeleteClick : undefined,
        onEdit,
        onResolve: onToggleResolve ? handleToggleResolveClick : undefined,
      }),
    [handleDeleteClick, handleToggleResolveClick, onDelete, onEdit, onToggleResolve],
  );

  if (!menuOptions.length && !onToggleResolve) {
    return null;
  }

  const hasOnlyResolveInMenu = menuOptions.length === 1 && menuOptions[0].id === 'resolve';

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions lint/a11y/useKeyWithClickEvents lint/performance/noJsxPropsBind: PLA-1724 — pre-existing debt, not introduced by the icon migration
    <div className="comment-menu flex items-center" onClick={(event) => event.stopPropagation()}>
      {onToggleResolve && (
        <IconButton
          adsIcon
          appearance="ghost"
          color="gray"
          data-testid="RESOLVE_DISCUSSION_BUTTON"
          icon={MessageCircleCheckIcon}
          label="Resolve discussion"
          onClick={handleToggleResolveClick}
          size="small"
        />
      )}
      {menuOptions.length > 0 && !hasOnlyResolveInMenu && (
        <DropdownMenuWithActionSheet
          isTitleHidden
          options={menuOptions}
          title="More comment options"
          trigger={
            <IconButton
              adsIcon
              appearance="ghost"
              color="gray"
              data-testid={DISCUSSION_COMMENT_MENU_TRIGGER}
              icon={EllipsisVerticalIcon}
              label="More options"
              size="small"
            />
          }
        />
      )}
    </div>
  );
});

CommentMenu.displayName = 'CommentMenu';
